import React from 'react';
import PropTypes from 'prop-types';
import StepModel from '../../models/stepModel';
import { Segment, Checkbox, Dropdown, Button, Flag, Menu, Tab, Divider, TextArea, Form, Grid, Input } from 'semantic-ui-react';
import { Trans, useTranslation } from 'react-i18next';
import Loading from '../../components/loading';
import StepTypeConfig from './widgetSteps/stepTypeConfig';

import { ToastContext } from '@weezevent/nacre';

function TranslationTextArea({ language, keyName, formTranslations, handleTextAreaChange }) {
    if (formTranslations[language][keyName] && formTranslations[language][keyName].custom) {
        return (
            <Form>
                <TextArea autoHeight name={language + '::' + keyName} value={formTranslations[language][keyName].value} onChange={handleTextAreaChange} />
            </Form>
        );
    }

    return null;
}

TranslationTextArea.propTypes = {
    language: PropTypes.string,
    keyName: PropTypes.string,
    formTranslations: PropTypes.array,
    handleTextAreaChange: PropTypes.func
};

function TranslationTabPane({ language, formTranslations, handleTextAreaChange }) {
    if (!formTranslations[language]) {
        return null;
    }

    return Object.keys(formTranslations[language]).map(key => {
        return (
            <div className="translationKeyContainer" key={language + '-' + key}>
                <label>
                    <Trans i18nKey="ticket.widget.key">Key</Trans>
                    <span className="trad-key">{key}</span>
                </label>
                <TranslationTextArea keyName={key} language={language} formTranslations={formTranslations} handleTextAreaChange={handleTextAreaChange} />
                <Divider />
            </div>
        );
    });
}
TranslationTabPane.propTypes = {
    language: PropTypes.string,
    formTranslations: PropTypes.array,
    handleTextAreaChange: PropTypes.func
};

function FormTranslations({ widget, languages, step, formTranslations, handleTextAreaChange }) {
    if (!languages || !step.translations?.wording) {
        return null;
    }

    let { default_language = 'en-GB' } = widget;
    let default_prefix = default_language.slice(0, 2);

    languages.sort((langA, langB) => {
        if (langA === default_language || langA === default_prefix) {
            return -1;
        }

        if (langB === default_language || langB === default_prefix) {
            return 1;
        }

        return langA < langB ? -1 : langA > langB ? 1 : 0;
    });

    let tabs = languages.map(language => {
        return {
            menuItem: (
                <Menu.Item key={language}>
                    <Flag name={language.length !== 4 ? language : language.split('-')[1].toLowerCase()} /> {language}
                </Menu.Item>
            ),
            render: function renderTab(props) {
                return (
                    <Tab.Pane>
                        <TranslationTabPane language={language} formTranslations={formTranslations} handleTextAreaChange={handleTextAreaChange} />
                    </Tab.Pane>
                );
            }
        };
    });
    return <Tab panes={tabs} />;
}

FormTranslations.propTypes = {
    languages: PropTypes.array,
    formTranslations: PropTypes.array,
    handleTextAreaChange: PropTypes.func,
    step: PropTypes.object,
    widget: PropTypes.object.isRequired
};

export default function StepContainer({ stepId = null, stepObject = null, organizationId, steps, widgetId, widget }) {
    const { t } = useTranslation();
    // In component to force translation to work
    const StepTypes = [
        {
            value: 'TextStep',
            text: t('ticket.widget.types.text_step')
        },
        {
            value: 'IdentificationStep',
            text: t('ticket.widget.types.identification_step')
        },
        {
            value: 'LogicalStep',
            text: t('ticket.widget.types.logical_step')
        },
        {
            value: 'ConfirmationStep',
            text: t('ticket.widget.types.confirmation_step')
        },
        {
            value: 'QueueStep',
            text: t('ticket.widget.types.queue_step')
        }
    ];

    const [optionsDropdown, setOptionsDropdown] = React.useState([]);

    // TODO: refacto using useForm
    const [successStep, setSuccessStep] = React.useState(null);
    const [errorStep, setErrorStep] = React.useState(null);
    const [previousAllowed, setPreviousAllowed] = React.useState(null);
    const [skipAllowed, setSkipAllowed] = React.useState(null);
    const [resetAllowed, setResetAllowed] = React.useState(null);
    const [formTranslations, setFormTranslations] = React.useState([]);
    const [config, setStepConfig] = React.useState('{}');
    const [languages, setLanguages] = React.useState(null);
    const [slug, setSlug] = React.useState('');
    const [stepType, setStepType] = React.useState('TextStep');

    const toast = React.useContext(ToastContext);

    const [step, loading, sync] = StepModel.useApiModel(
        {
            id: stepId,
            organizationId,
            widgetId,
            onSuccess: response => {
                setSuccessStep(response.success_step);
                setErrorStep(response.error_step);
                setPreviousAllowed(response.previous_allowed);
                setSkipAllowed(response.skip_allowed);
                setStepType(response.step_class);
                setSlug(response.slug);
                setResetAllowed(response.reset_allowed);
                // Set config in JSON humand readable format
                setStepConfig(JSON.stringify(response.config, null, 4));

                const mapToSteps = ({ id, slug }) => ({ key: id, text: slug, value: id });
                let tmpOptionsDropdown = steps ? steps.map(mapToSteps) : null;
                setOptionsDropdown(tmpOptionsDropdown);
            }
        },
        [stepId]
    );

    //Init languages
    React.useEffect(() => {
        if (step) {
            let tmpLanguages = [];
            const customTranslation = step.translations;
            if (customTranslation['wording']) {
                tmpLanguages = [...tmpLanguages, ...Object.keys(customTranslation['wording'])];
            }
            setLanguages(tmpLanguages);
        }
    }, [step]);

    //init translation
    React.useEffect(() => {
        if (languages) {
            setFormTranslations(initTranslations());
        }
    }, [languages]);

    const initTranslations = React.useCallback(() => {
        const obj = [];

        if (step.translations?.wording) {
            languages.map(language => {
                obj[language] = {};
                for (let k of Object.keys(step.translations.wording[language])) {
                    obj[language][k] = {
                        key: k,
                        value: '',
                        custom: false
                    };
                }
                return null;
            });

            const customTranslation = step.translations;
            if (customTranslation['wording']) {
                Object.entries(customTranslation['wording']).map(([language]) => {
                    Object.entries(customTranslation['wording'][language]).map(([key]) => {
                        obj[language][key].value = customTranslation['wording'][language][key];
                        obj[language][key].custom = true;
                        return null;
                    });
                    return null;
                });
            }
        }
        return obj;
    }, [step, languages]);

    const onSubmit = React.useCallback(() => {
        step.success_step = successStep;
        step.error_step = errorStep;
        step.skip_allowed = skipAllowed;
        step.previous_allowed = previousAllowed;
        step.slug = slug;
        step.step_class = stepType;
        step.reset_allowed = resetAllowed;
        let translations = {};
        Object.entries(formTranslations).map(([language]) => {
            Object.entries(formTranslations[language]).map(([key]) => {
                if (!translations[language]) {
                    translations[language] = {};
                }
                if (!translations[language][key]) {
                    translations[language][key] = {};
                }
                translations[language][key] = formTranslations[language][key].value;
                return null;
            });
            return null;
        });

        step.translations.wording = translations.default;
        delete translations.default;
        if (Object.keys(translations).length > 0) {
            step.translations.wording = translations;
        }

        try {
            step.config = JSON.parse(config);
        } catch (e) {
            return toast.error(t('ticket.widget.jsonError'));
        }

        step.save({
            organizationId,
            widgetId
        })
            .then(() => {
                toast.success(t('ticket.widget.configSaved'));
                sync();
            })
            .catch(e => {
                toast.error(t('ticket.widget.saveError'));
            });
    }, [
        config,
        step,
        formTranslations,
        skipAllowed,
        previousAllowed,
        successStep,
        errorStep,
        resetAllowed,
        slug,
        stepId,
        languages,
        stepType,
        loading,
        optionsDropdown,
        widgetId,
        sync
    ]);

    const handleTextAreaChange = React.useCallback(
        (e, data) => {
            const array = data.name.split('::');
            const language = array[0];
            const key = array[1];

            let tmpFormTranslations = { ...formTranslations };
            tmpFormTranslations[language][key].value = data.value;
            setFormTranslations(tmpFormTranslations);
        },
        [formTranslations]
    );

    const lastParsedConfig = React.useRef({});
    const parsedConfig = React.useMemo(() => {
        try {
            let conf = JSON.parse(config);
            lastParsedConfig.current = conf;
            return lastParsedConfig.current;
        } catch (e) {
            // Do nothing
            return lastParsedConfig.current;
        }
        // I would use a finally clause, but linter won't allow
    }, [lastParsedConfig, config]);

    if (loading || !step) {
        return <Loading />;
    }

    return (
        <div>
            <Segment>
                <Button primary type="submit" onClick={onSubmit}>
                    <Trans i18nKey="ticket.common.action.save">Save</Trans>
                </Button>
            </Segment>
            <Grid widths="equal" columns={2}>
                <Grid.Column>
                    <Form.Field>
                        <label>{t(`ticket.widget.step_class`)}</label>
                        <Dropdown clearable fluid search options={StepTypes} selection value={stepType} onChange={({ target: { value } }) => setStepType(value)} />
                    </Form.Field>
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.widget.slug`)}</label>
                    <Input type="text" value={slug} onChange={({ target: { value } }) => setSlug(value)} />
                </Grid.Column>
            </Grid>
            <Grid widths="equal" columns={2}>
                <Grid.Column>
                    <label>{t(`ticket.widget.success_step`)}</label>
                    <Dropdown clearable fluid search options={optionsDropdown} selection onChange={(synth, data) => setSuccessStep(data.value)} value={successStep} />
                    <Checkbox checked={previousAllowed} label={t('ticket.widget.previous_allowed')} onChange={(synth, data) => setPreviousAllowed(!previousAllowed)} />
                </Grid.Column>
                <Grid.Column>
                    <label>{t(`ticket.widget.error_step`)}</label>
                    <Dropdown clearable fluid search options={optionsDropdown} selection onChange={(synth, data) => setErrorStep(data.value)} value={errorStep} />
                    <Checkbox checked={skipAllowed} label={t('ticket.widget.skip_allowed')} onChange={(synth, data) => setSkipAllowed(!skipAllowed)} />
                </Grid.Column>
                <Grid.Column>
                    <Checkbox checked={resetAllowed} label={t('ticket.widget.reset_allowed')} onChange={(synth, data) => setResetAllowed(!resetAllowed)} />
                </Grid.Column>
            </Grid>
            <StepTypeConfig
                steps={steps}
                step={step}
                widget={widget}
                config={parsedConfig}
                onConfigUpdate={newConfig => setStepConfig(JSON.stringify(newConfig, null, 4))}
                onUpdate={() => sync()}
            />
            <Segment basic>
                <h4>{t('ticket.widget.translations')}</h4>
                <FormTranslations widget={widget} languages={languages} step={step} formTranslations={formTranslations} handleTextAreaChange={handleTextAreaChange} />
            </Segment>
            <Segment basic>
                <h4>{t('ticket.widget.config')}</h4>
                <Form>
                    <TextArea
                        value={config}
                        style={{ minHeight: '250px' }}
                        onChange={({ target: { value } }) => {
                            setStepConfig(value);
                        }}
                    />
                </Form>
            </Segment>
        </div>
    );
}

//stepId = null, stepObject = null, organizationId, steps, widgetId
StepContainer.propTypes = {
    stepId: PropTypes.number,
    stepObject: PropTypes.object,
    organizationId: PropTypes.number.isRequired,
    steps: PropTypes.array.isRequired,
    widgetId: PropTypes.number.isRequired,
    widget: PropTypes.object.isRequired
};
